import { IconDot } from "@/assets/icons/geist/IconDot";
import type { Size } from "@/assets/icons/utils";
import { cn, type PropsWithClassName } from "@/components/podkit/lib/cn";
import { Tooltip } from "@/components/Tooltip";
import { useEffect, useState, type FC } from "react";

export type StatusDotAnimation = "none" | "ping" | "heartbeat" | "fade" | "spin" | "pulse";
export type StatusDotVariant = "solid" | "small" | "ring";
export type StatusDotColor = "green" | "brand" | "orange" | "red" | "gray";

export type StatusDotProps = {
    size: Size;
    color: StatusDotColor;
    variant?: StatusDotVariant;
    animation?: StatusDotAnimation;
    tooltip?: string;
} & PropsWithClassName;

const colorToClass: Record<StatusDotColor, string> = {
    green: "text-content-green",
    brand: "text-content-brand",
    orange: "text-content-orange",
    red: "text-content-red",
    gray: "text-content-tertiary",
};

export const StatusDot: FC<StatusDotProps> = ({
    size,
    color,
    variant = "solid",
    animation = "none",
    tooltip,
    className,
}) => {
    const colorClass = colorToClass[color];

    const content = (
        <StatusDotContent
            size={size}
            colorClass={colorClass}
            variant={variant}
            animation={animation}
            tooltip={tooltip}
            className={className}
        />
    );

    if (tooltip) {
        return (
            <Tooltip content={tooltip} usePortal>
                {content}
            </Tooltip>
        );
    }

    return content;
};

type StatusDotContentProps = {
    size: Size;
    colorClass: string;
    variant: StatusDotVariant;
    animation: StatusDotAnimation;
    tooltip?: string;
    className?: string;
};

const StatusDotContent: FC<StatusDotContentProps> = ({ size, colorClass, variant, animation, tooltip, className }) => {
    // For animated states with lg size, render custom SVG
    if (animation !== "none" && size === "lg") {
        if (animation === "spin") {
            return (
                <svg
                    aria-label={tooltip}
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    className={cn("block", colorClass, className)}
                    data-testid="status-dot"
                >
                    <style>
                        {`
                            @keyframes spin-ring {
                                0% {
                                    transform: rotate(0deg);
                                }
                                100% {
                                    transform: rotate(360deg);
                                }
                            }
                            .spin-ring {
                                transform-origin: center;
                                animation: spin-ring 2s linear infinite;
                            }
                        `}
                    </style>
                    {/* Static inner dot with outer ring */}
                    <circle
                        cx="12"
                        cy="12"
                        r="3.5"
                        fill="currentColor"
                        stroke="currentColor"
                        strokeOpacity="0.15"
                        strokeWidth="6"
                    />
                    {/* Spinning arc - bordering the outer edge of the ring */}
                    <circle
                        className="spin-ring"
                        cx="12"
                        cy="12"
                        r="6"
                        fill="none"
                        stroke="currentColor"
                        strokeOpacity="0.5"
                        strokeWidth="1"
                        strokeLinecap="round"
                        strokeDasharray="7 31"
                    />
                </svg>
            );
        }

        if (animation === "fade") {
            return (
                <svg
                    aria-label={tooltip}
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    className={cn("block", colorClass, className)}
                    data-testid="status-dot"
                >
                    <style>
                        {`
                            @keyframes fade-pulse {
                                0%, 100% {
                                    opacity: 0.9;
                                }
                                50% {
                                    opacity: 0.5;
                                }
                            }
                            .fade-dot {
                                animation: fade-pulse 2.5s ease-in-out infinite;
                            }
                        `}
                    </style>
                    <circle className="fade-dot" cx="12" cy="12" r="4" fill="currentColor" />
                </svg>
            );
        }

        if (animation === "heartbeat") {
            return (
                <svg
                    aria-label={tooltip}
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    className={cn("block", colorClass, className)}
                    data-testid="status-dot"
                >
                    <style>
                        {`
                            @keyframes hb-pulse {
                                0% { r: 4; opacity: 0.4; }
                                100% { r: 10; opacity: 0; }
                            }
                            .hb-1 {
                                animation: hb-pulse 0.5s ease-out infinite;
                                animation-duration: 3s;
                            }
                            .hb-2 {
                                animation: hb-pulse 0.5s ease-out infinite;
                                animation-delay: 1.2s;
                                animation-duration: 3s;
                            }
                            .hb-3 {
                                animation: hb-pulse 0.5s ease-out infinite;
                                animation-delay: 1.85s;
                                animation-duration: 3s;
                            }
                        `}
                    </style>
                    <circle className="hb-1" cx="12" cy="12" r="4" fill="currentColor" />
                    <circle className="hb-2" cx="12" cy="12" r="4" fill="currentColor" />
                    <circle className="hb-3" cx="12" cy="12" r="4" fill="currentColor" />
                    <circle cx="12" cy="12" r="4" fill="currentColor" />
                </svg>
            );
        }

        if (animation === "pulse") {
            return <PulseDot colorClass={colorClass} tooltip={tooltip} className={className} />;
        }

        // ping animation (default for animated)
        return (
            <svg
                aria-label={tooltip}
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                className={cn("block", colorClass, className)}
                data-testid="status-dot"
            >
                <style>
                    {`
                        @keyframes raindrop {
                            0% {
                                r: 4;
                                opacity: 0.4;
                            }
                            100% {
                                r: 10;
                                opacity: 0;
                            }
                        }
                        .raindrop {
                            animation: raindrop 1.5s ease-out infinite;
                        }
                    `}
                </style>
                <circle className="raindrop" cx="12" cy="12" r="4" fill="currentColor" />
                <circle cx="12" cy="12" r="4" fill="currentColor" />
            </svg>
        );
    }

    // Solid dot without outer ring
    if (variant === "solid" && size === "lg") {
        return (
            <svg
                aria-label={tooltip}
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                className={cn("block", colorClass, className)}
                data-testid="status-dot"
            >
                <circle cx="12" cy="12" r="4" fill="currentColor" />
            </svg>
        );
    }

    // Small dot for inactive/stopped states
    if (variant === "small" && size === "lg") {
        return (
            <svg
                aria-label={tooltip}
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                className={cn("block", colorClass, className)}
                data-testid="status-dot"
            >
                <circle cx="12" cy="12" r="2" fill="currentColor" />
            </svg>
        );
    }

    // Default: use IconDot for ring variant or non-lg sizes
    return (
        <IconDot
            aria-label={tooltip}
            size={size}
            className={cn("block", colorClass, className)}
            data-testid="status-dot"
        />
    );
};

/**
 * 3 expanding rings that play once on mount then disappear,
 * leaving a steady solid dot. The parent must use `visibility: hidden`
 * (not `display: none`) to hide this component, otherwise the browser
 * restarts the CSS animation when the element becomes visible again.
 */
const PulseDot: FC<{ colorClass: string; tooltip?: string; className?: string }> = ({
    colorClass,
    tooltip,
    className,
}) => {
    const [showRings, setShowRings] = useState(true);

    useEffect(() => {
        // Last ring starts at 1.6s, animation lasts 0.6s → done at 2.2s
        const timer = setTimeout(() => setShowRings(false), 2400);
        return () => clearTimeout(timer);
    }, []);

    return (
        <svg
            aria-label={tooltip}
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            className={cn("block", colorClass, className)}
            data-testid="status-dot"
        >
            {showRings && (
                <>
                    <style>
                        {`
                            @keyframes pulse-ring {
                                0% { r: 4; opacity: 0.4; }
                                100% { r: 10; opacity: 0; }
                            }
                        `}
                    </style>
                    <circle
                        cx="12"
                        cy="12"
                        r="4"
                        fill="currentColor"
                        style={{ animation: "pulse-ring 0.6s ease-out forwards" }}
                    />
                    <circle
                        cx="12"
                        cy="12"
                        r="4"
                        fill="currentColor"
                        style={{ animation: "pulse-ring 0.6s ease-out 0.8s forwards", opacity: 0 }}
                    />
                    <circle
                        cx="12"
                        cy="12"
                        r="4"
                        fill="currentColor"
                        style={{ animation: "pulse-ring 0.6s ease-out 1.6s forwards", opacity: 0 }}
                    />
                </>
            )}
            <circle cx="12" cy="12" r="4" fill="currentColor" />
        </svg>
    );
};
